import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Search, User } from "lucide-react";

export default function ZoneOwnerHistory() {
  const [q, setQ] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return employees.slice(0, 20);
    return employees.filter((u) =>
      (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s)
    ).slice(0, 20);
  }, [q, employees]);

  const load = useCallback(async (uid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/zone_owner_history", { params: { user_id: uid } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="zone-owner-history">
      <Link to="/display" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Display Checks</Link>
      <div>
        <div className="label-caps">Reporting</div>
        <h1 className="font-heading text-3xl sm:text-4xl">Zone Owner History</h1>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">Type a name to see every past inspection that person was accountable for as a Zone Owner.</div>
      </div>

      <div className="hos-card p-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
          <input className="hos-input pl-9" placeholder="Search employees…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="zoh-search"/>
        </div>
        <div className="mt-3 max-h-56 overflow-y-auto border border-[color:var(--border)] rounded-md">
          {filtered.map((u) => (
            <button key={u.id}
                    onClick={() => { setSelected(u); load(u.id); }}
                    className={`w-full text-left px-3 py-2 text-sm border-b border-[color:var(--border)]/50 last:border-0 flex items-center justify-between ${selected?.id === u.id ? "bg-[color:var(--gold)]/10" : "hover:bg-[color:var(--bg-surface-2)]"}`}
                    data-testid={`zoh-user-${u.id}`}>
              <span className="flex items-center gap-2"><User size={14}/><span className="font-semibold">{u.name}</span>
                <span className="text-[10px] uppercase text-[color:var(--text-secondary)]">{u.role}</span>
              </span>
              <span className="text-xs text-[color:var(--text-secondary)]">{u.email}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-[color:var(--text-secondary)]">Loading…</div>}
      {data && (
        <div className="hos-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-caps">Selected</div>
              <div className="font-heading text-xl">{data.user?.name || "—"}</div>
              <div className="text-xs text-[color:var(--text-secondary)]">{data.user?.email}</div>
            </div>
            <div className="text-sm text-[color:var(--gold)]">{data.inspections.length} inspection(s)</div>
          </div>
          {data.inspections.length === 0 ? (
            <div className="text-sm text-[color:var(--text-secondary)]">No inspections found for this user.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left label-caps">
                  <tr><th className="p-2">Date</th><th>Check</th><th>Zone</th><th>Fandoms</th><th>Condition</th><th>Cleanliness</th><th>Issues</th><th className="text-right">Score</th><th></th></tr>
                </thead>
                <tbody>
                  {data.inspections.map((r, i) => (
                    <tr key={i} className="border-t border-[color:var(--border)]">
                      <td className="p-2 whitespace-nowrap">{r.inspection_date}</td>
                      <td>{r.check_number}</td>
                      <td>{r.zone_name}</td>
                      <td className="text-xs text-[color:var(--text-secondary)]">{(r.fandom_names || []).join(", ") || "—"}</td>
                      <td className="capitalize">{(r.condition || "—").replace(/_/g, " ")}</td>
                      <td className="capitalize">{(r.cleanliness_status || "—").replace(/_/g, " ")}</td>
                      <td>{r.issues_count}</td>
                      <td className="text-right font-heading text-[color:var(--gold)]">{r.final_display_score ?? r.display_score ?? "—"}</td>
                      <td><Link to={`/display/${r.check_id}`} className="text-[color:var(--gold)] text-xs underline">Open</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
